import { useMemo, useState } from "react";
import { FiSearch, FiChevronDown, FiChevronUp, FiCheck } from "react-icons/fi";
import { useAgentsWithOfficeQuery } from "apis/agent";

// Ported from beleef-services-frontend components/onboarding/WorkWithSelect.js
const TABS = [
  { value: "office", label: "By Office" },
  { value: "agent", label: "By Agent" },
];

const EMPTY = { offices: [], agents: [] };

// offices/agents come back populated (objects) on read, raw ids on write
const toIds = (list) => (list || []).map((x) => String(x?._id || x));

/** Stored company doc → form value. */
export const toWorkWithValue = (company = {}) => ({
  offices: toIds(company.offices),
  agents: toIds(company.agents),
});

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase();

const Avatar = ({ src, name }) =>
  src ? (
    <img
      src={src}
      alt={name}
      loading="lazy"
      decoding="async"
      width={36}
      height={36}
      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
    />
  ) : (
    <span className="w-9 h-9 rounded-full bg-lightgray text-darkergray text-xs font-semibold flex items-center justify-center flex-shrink-0">
      {getInitials(name)}
    </span>
  );

/**
 * "Who does this company work with?" — two mutually exclusive modes:
 *
 *   By Office → select whole offices (applies to all their agents)
 *   By Agent  → select individual agents
 *
 * Choosing offices clears any agent selection and vice versa, so the value
 * is always one or the other.
 *
 * Controlled value: { offices: string[], agents: string[] } (only one
 * non-empty at a time).
 */
const WorkWithSelect = ({ value = EMPTY, onChange, error }) => {
  const { data: agents = [], isLoading } = useAgentsWithOfficeQuery();

  const [tab, setTab] = useState(() =>
    value?.agents?.length ? "agent" : "office",
  );
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  const selectedOffices = useMemo(() => new Set(value?.offices || []), [value]);
  const selectedAgents = useMemo(() => new Set(value?.agents || []), [value]);

  // Group agents by office
  const offices = useMemo(() => {
    const map = new Map();
    for (const a of agents) {
      const off = a.office;
      if (!off?._id) continue;
      if (!map.has(off._id)) map.set(off._id, { ...off, agents: [] });
      map.get(off._id).agents.push(a);
    }
    return [...map.values()];
  }, [agents]);

  const q = query.trim().toLowerCase();

  const filteredOffices = useMemo(() => {
    if (!q) return offices;
    return offices.filter((o) =>
      [o.displayName, o.name, o.address]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q)),
    );
  }, [offices, q]);

  const filteredAgents = useMemo(() => {
    if (!q) return agents;
    return agents.filter((a) =>
      [a.name, a.email, a.office?.name, a.office?.displayName]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q)),
    );
  }, [agents, q]);

  // ─── Exclusive selection ───
  const toggleOffice = (id) => {
    const next = new Set(selectedOffices);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ offices: [...next], agents: [] }); // clears agent picks
  };

  const toggleAgent = (id) => {
    const next = new Set(selectedAgents);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ offices: [], agents: [...next] }); // clears office picks
  };

  const toggleExpand = (officeId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(officeId)) next.delete(officeId);
      else next.add(officeId);
      return next;
    });
  };

  // "Select all" + count operate on the active tab (respecting search)
  const visibleOfficeIds = filteredOffices.map((o) => o._id);
  const visibleAgentIds = filteredAgents.map((a) => a._id);

  const allOfficesSelected =
    visibleOfficeIds.length > 0 &&
    visibleOfficeIds.every((id) => selectedOffices.has(id));
  const allAgentsSelected =
    visibleAgentIds.length > 0 &&
    visibleAgentIds.every((id) => selectedAgents.has(id));
  const allVisibleSelected =
    tab === "office" ? allOfficesSelected : allAgentsSelected;

  const toggleSelectAll = () => {
    if (tab === "office") {
      const next = new Set(selectedOffices);
      visibleOfficeIds.forEach((id) =>
        allOfficesSelected ? next.delete(id) : next.add(id),
      );
      onChange({ offices: [...next], agents: [] });
    } else {
      const next = new Set(selectedAgents);
      visibleAgentIds.forEach((id) =>
        allAgentsSelected ? next.delete(id) : next.add(id),
      );
      onChange({ offices: [], agents: [...next] });
    }
  };

  const countLabel =
    tab === "office"
      ? `${selectedOffices.size} office${
          selectedOffices.size === 1 ? "" : "s"
        } selected`
      : `${selectedAgents.size} agent${
          selectedAgents.size === 1 ? "" : "s"
        } selected`;

  const officeSubLabel = (agent) =>
    agent.office?.displayName || agent.office?.name || "";

  // ─── Agent row: selectable (By Agent) or read-only (inside an office) ───
  const renderAgentRow = (agent, { readOnly = false } = {}) => {
    if (readOnly) {
      return (
        <div
          key={agent._id}
          className="w-full flex items-center gap-3 px-4 py-2"
        >
          <Avatar src={agent.picture} name={agent.name} />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium truncate">
              {agent.name}
            </span>
            <span className="block text-xs text-darkgray truncate">
              {officeSubLabel(agent)}
            </span>
          </span>
        </div>
      );
    }

    const isSel = selectedAgents.has(agent._id);
    return (
      <button
        key={agent._id}
        type="button"
        onClick={() => toggleAgent(agent._id)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
          isSel
            ? "border-black bg-lightergray"
            : "border-mediumgray hover:border-darkgray bg-white"
        }`}
      >
        <span
          className={`flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${
            isSel ? "bg-black border-black" : "border-mediumgray"
          }`}
        >
          {isSel && <FiCheck className="w-3.5 h-3.5 text-white" />}
        </span>
        <Avatar src={agent.picture} name={agent.name} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium truncate">
            {agent.name}
          </span>
          <span className="block text-xs text-darkgray truncate">
            {officeSubLabel(agent)}
          </span>
        </span>
      </button>
    );
  };

  if (isLoading) {
    return <p className="text-sm text-darkgray py-4">Loading…</p>;
  }

  return (
    <div className="space-y-3">
      {/* Tabs */}
      <div className="flex border-b border-mediumgray">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => {
              setTab(t.value);
              setQuery("");
            }}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.value
                ? "border-black text-black"
                : "border-transparent text-darkgray hover:text-black"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-darkgray pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            tab === "office" ? "Search offices..." : "Search agents..."
          }
          className="form-input border border-mediumgray pl-9"
        />
      </div>

      {/* Select all + count */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={toggleSelectAll}
          className="text-sm text-black underline"
        >
          {allVisibleSelected ? "Deselect all" : "Select all"}
        </button>
        <span className="text-sm text-darkgray">{countLabel}</span>
      </div>

      {/* List */}
      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {tab === "office" ? (
          filteredOffices.length === 0 ? (
            <p className="text-sm text-darkgray py-4 text-center">
              No offices found
            </p>
          ) : (
            filteredOffices.map((office) => {
              const isSel = selectedOffices.has(office._id);
              const isExpanded = expanded.has(office._id);
              return (
                <div
                  key={office._id}
                  className={`rounded-xl border overflow-hidden ${
                    isSel ? "border-black" : "border-mediumgray"
                  }`}
                >
                  <div
                    className={`flex items-center gap-3 px-4 py-3 ${
                      isSel ? "bg-black" : "bg-white"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleOffice(office._id)}
                      className={`flex items-center gap-3 flex-1 min-w-0 text-left ${
                        isSel ? "text-white" : "text-black"
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${
                          isSel ? "border-white" : "border-mediumgray"
                        }`}
                      >
                        {isSel && (
                          <FiCheck className="w-3.5 h-3.5 text-white" />
                        )}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span
                          className={`block text-sm font-semibold truncate ${
                            isSel ? "text-white" : "text-black"
                          }`}
                        >
                          {office.displayName || office.name}
                        </span>
                        {office.address && (
                          <span
                            className={`block text-xs truncate ${
                              isSel ? "text-gray-300" : "text-darkgray"
                            }`}
                          >
                            {office.address}
                          </span>
                        )}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleExpand(office._id)}
                      className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                        isSel ? "text-gray-300" : "text-darkgray"
                      }`}
                    >
                      {office.agents.length} agent
                      {office.agents.length === 1 ? "" : "s"}
                      {isExpanded ? (
                        <FiChevronUp className="w-4 h-4" />
                      ) : (
                        <FiChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="bg-lightergray p-2 divide-y divide-mediumgray">
                      {office.agents.map((agent) =>
                        renderAgentRow(agent, { readOnly: true }),
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )
        ) : filteredAgents.length === 0 ? (
          <p className="text-sm text-darkgray py-4 text-center">
            No agents found
          </p>
        ) : (
          filteredAgents.map((agent) => renderAgentRow(agent))
        )}
      </div>

      {error && <span className="form-error-message">{error}</span>}
    </div>
  );
};

export default WorkWithSelect;
